/**
 * Leaguemate Scouting — deep-dive orchestrator (Tier 2, on click).
 *
 * Given ONE manager (from S1/S2's foundation), pull transactions + picks/drafts
 * for their dynasty/keeper TX set and derive the COMPLETE fingerprint:
 *   S3   — trade-style, FAAB, reachability, net-affinity (believers + don't-value)
 *   S3.5 — win-now/rebuild, positional lean, archetype crown (nullable)
 * Attaches `manager.deepDive` and returns it.
 */

import { getSleeperPlayersMap } from "../sleeperImport.js";
import { fetchValueMap } from "./scoutValues.js";
import {
  selectTxCandidateSet,
  fetchTransactionsForSet,
  fetchPicksDraftsForSet,
} from "./scoutDeepFetch.js";
import { computeTradeStyle, computeReachability } from "./scoutTradeStyle.js";
import { computeFaab } from "./scoutFaab.js";
import { computeTradeAway, combineNetAffinity } from "./scoutTradeAway.js";
import { computeRebuild } from "./scoutRebuild.js";
import { computePositional } from "./scoutPositional.js";
import { crownArchetype } from "./scoutArchetype.js";
import { NET_AFFINITY_WEIGHTS } from "./scoutConfig.js";

const noop = () => {};

export async function buildOwnerDeepDive(
  manager,
  { profile, currentSeason, playersMap = null, valueMap = null, onProgress = noop, nowMs = Date.now() } = {}
) {
  const pmap = playersMap || (await getSleeperPlayersMap());
  let vmap = valueMap;
  if (!vmap) {
    if (!profile) throw new Error("buildOwnerDeepDive needs a valueMap or a target format profile");
    vmap = (await fetchValueMap(profile)).valueMap;
  }

  // Select the TX set once; both pulls share it (identical league set §7c).
  const txSet = selectTxCandidateSet(manager.affinity);
  onProgress("Pulling transactions + picks…", 0, 1);
  const [leagues, pickRows] = await Promise.all([
    fetchTransactionsForSet(txSet, { onProgress }),
    fetchPicksDraftsForSet(txSet, { onProgress }),
  ]);

  // S3 signals.
  const tradeStyle = computeTradeStyle(leagues, manager.user_id, vmap, { nowMs });
  const faab = computeFaab(leagues);
  const reachability = computeReachability(leagues, { nowMs });
  const ta = computeTradeAway(leagues, pmap, vmap);
  const adjustedBelievers = combineNetAffinity(manager.believers || [], ta.acqByPlayer, ta.awayByPlayer);

  // S3.5 dimensions. Roster rows = the tx-set transaction rows (carry players[]).
  const season = currentSeason || profile?.season || manager?._targetSeason || "2026";
  const rebuild = computeRebuild(pickRows, leagues, pmap, { currentSeason: season });
  const positional = computePositional(leagues, pickRows, pmap);

  const archetype = crownArchetype({
    tradeStyle, faab, reachability, rebuild, positional, affinityMeta: manager.affinityMeta || {},
  });

  const deepDive = {
    scope: "full-fingerprint", // S3 + S3.5; reach-vs-consensus draft posture still deferred (§7d)
    txLeagueCount: leagues.length,
    txLeagues: leagues.map((l) => ({
      league_id: l.league_id, league_name: l.league_name, season: l.season,
      leagueType: l.leagueType, similarity: l.similarity, txCount: l.completeTx.length,
    })),
    tradeStyle,
    faab,
    reachability,
    rebuild,
    positional,
    archetype, // nullable: { archetype: null, reason } when no axis dominates / thin
    netAffinity: {
      adjustedBelievers,
      dontValue: ta.dontValue,
      tradeCount: ta.tradeCount,
      weights: NET_AFFINITY_WEIGHTS,
    },
  };

  manager.deepDive = deepDive;
  return deepDive;
}

export { selectTxCandidateSet };
